import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Calculate as CalculateIcon } from '@mui/icons-material';
import { APIProvider } from '@vis.gl/react-google-maps';
import { httpsCallable } from 'firebase/functions';

import { functions } from '../../firebase/config';
import PlaceAutocomplete from '../common/PlaceAutocomplete';

const VEHICLE_TYPE_LABELS = {
  1: 'PKW (4 Sitze)',
  2: 'Großraum (6 Sitze)',
  3: 'Kleinbus (7 Sitze)',
  4: 'Kleinbus (8 Sitze)',
};

const euro = (cents) => `${(cents / 100).toFixed(2).replace('.', ',')} €`;

// Festpreis vor der Fahrt (Mietwagen) — Einstellungen → Tarif.
// nextcab_mobile/docs/general/mietwagen_festpreis_modus_konzept.md §11
/**
 * Vorschau-Rechner: echte Strecke → Festpreis je Fahrzeugtyp mit
 * Aufschlüsselung. Rechnet mit den GESPEICHERTEN Werten (Cloud Function).
 */
const FestpreisVorschau = () => {
  const [pickup, setPickup] = useState({ address: '', location: null });
  const [destination, setDestination] = useState({ address: '', location: null });
  const [abholzeit, setAbholzeit] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const canCalculate = pickup.location && destination.location && !loading;

  const calculate = async () => {
    setLoading(true);
    setError(null);
    try {
      const scheduledTimeMs = abholzeit ? new Date(abholzeit).getTime() : null;
      const response = await httpsCallable(functions, 'quoteRide')({
        preview: true,
        pickup: { lat: pickup.location.latitude, lng: pickup.location.longitude, address: pickup.address },
        destination: { lat: destination.location.latitude, lng: destination.location.longitude, address: destination.address },
        scheduledTimeMs,
      });
      setResult(response.data);
    } catch (e) {
      setResult(null);
      setError(e?.message || 'Berechnung fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  const fieldFor = (state, setState, label, role) => (
    <PlaceAutocomplete
      label={label}
      subDestinationRole={role}
      requireLocation
      location={state.location}
      value={state.address}
      onChange={(e) => {
        const address = e.target.value;
        setState((s) => ({ address, location: address === s.address ? s.location : null }));
      }}
      onPlaceSelect={(address, location) => setState({ address, location })}
      size="small"
      fullWidth
    />
  );

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <Box sx={{ mt: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          <CalculateIcon sx={{ verticalAlign: 'middle', mr: 1 }} fontSize="small" />
          Vorschau-Rechner
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Rechnet wie die App mit den gespeicherten Tarifwerten — Änderungen oben vorher speichern.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>{fieldFor(pickup, setPickup, 'Abholort', 'pickup')}</Grid>
          <Grid item xs={12} md={5}>{fieldFor(destination, setDestination, 'Ziel', 'destination')}</Grid>
          <Grid item xs={12} md={2}>
            <TextField
              size="small" fullWidth type="datetime-local" label="Abholzeit" value={abholzeit}
              onChange={(e) => setAbholzeit(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="leer = jetzt"
            />
          </Grid>
        </Grid>
        <Button
          sx={{ mt: 2 }} variant="outlined" onClick={calculate} disabled={!canCalculate}
          startIcon={loading ? <CircularProgress size={18} /> : <CalculateIcon />}
        >
          Festpreis berechnen
        </Button>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {result && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {String(result.distanceKm).replace('.', ',')} km · {result.durationMin} min ·{' '}
              {result.night ? 'Nachttarif' : 'Tagtarif'}
            </Typography>
            {/* Wende-Prüfung (Konzept §5.6): Strecke ab der Straße statt mit Wendeumweg */}
            {result.wende?.variante === 'strassenpunkt' && Number(result.wende.basisKm) > Number(result.distanceKm) && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Wende-Prüfung: {(Number(result.wende.basisKm) - Number(result.distanceKm)).toFixed(1).replace('.', ',')} km
                kürzer gerechnet als die Route ab der Adresse ({String(result.wende.basisKm).replace('.', ',')} km),
                weil der Wagen an der Straße wenden kann.
              </Typography>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fahrzeug</TableCell>
                  <TableCell align="right">Festpreis</TableCell>
                  <TableCell>Zusammensetzung</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(result.preise || {}).map(([vt, q]) => (
                  <TableRow key={vt}>
                    <TableCell>{VEHICLE_TYPE_LABELS[vt] || `Typ ${vt}`}</TableCell>
                    <TableCell align="right"><strong>{euro(q.cents)}</strong></TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {q.quelle === 'festpreisliste'
                          ? `Festpreisliste (${q.festpreisLabel || ''}) ${euro(q.basisCents)}`
                          : `Tarif ${euro(q.basisCents)}`}
                        {q.zuschlagCents > 0 && ` + Zuschlag ${euro(q.zuschlagCents)}`}
                        {q.anfahrtCents > 0 && ` + Anfahrt ${euro(q.anfahrtCents)}`}
                        {q.untergrenze === 'mindestpreis' && ' · Mindestfahrpreis greift'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </APIProvider>
  );
};

export default FestpreisVorschau;
